/**
 * Who is looking, for the screens that differ between FTC personnel and office
 * users — the Breakdowns tab shows FTC its intake cues ("Ask", "N to ask the
 * carrier", the fill-in links) and shows offices the breakdown as a result.
 *
 * The same rule as the Sidebar (Sidebar.js, checkPracticeAdmin): a super admin
 * counts as FTC, since they reach every FTC surface, and so does anyone holding
 * a role whose name contains "FTC-Personnel" (one per office they cover). One
 * request per signed-in user per page load, shared by every caller.
 */
import { useEffect, useState } from "react";

import { controller } from "../controller";

/** `{ isSuperAdmin, isFtc }` from /clinics/check-practice-admin/'s `result`. */
export function viewerRoleFrom(result) {
  if (result === "super_admin") return { isSuperAdmin: true, isFtc: true };
  const roles = Array.isArray(result) ? result : [];
  return {
    isSuperAdmin: false,
    isFtc: roles.some((name) => typeof name === "string" && name.indexOf("FTC-Personnel") !== -1),
  };
}

/**
 * Whether a breakdown is shown with the FTC intake layout ("Ask", "N to ask
 * the carrier", fill-in links, Patient | Member | Plan details + Coverage
 * conditions) or as the office reads it (people as closed cards, one Plan
 * details of answered facts). The intake layout is for FTC personnel and super
 * admins on our own surfaces; opened from the office's Insurance Verification
 * page (`readerMode`), everyone sees what the office sees.
 */
export function showsIntake(viewer, readerMode) {
  return !!(viewer && viewer.ready && viewer.isFtc) && !readerMode;
}

const NOBODY = { isSuperAdmin: false, isFtc: false };
let cached = { token: null, promise: null };

function signedInToken() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return (user && user.key) || "";
  } catch (e) {
    return "";
  }
}

/** The viewer's role, fetched once per signed-in user. Never rejects. */
export function loadViewerRole() {
  const token = signedInToken();
  if (!cached.promise || cached.token !== token) {
    const promise = controller.checkPracticeAdmin()
      .then((res) => viewerRoleFrom(res && res.data ? res.data.result : null))
      .catch(() => {
        // Don't cache a failure: the next caller asks again.
        if (cached.promise === promise) cached = { token: null, promise: null };
        return NOBODY;
      });
    cached = { token, promise };
  }
  return cached.promise;
}

/** For tests: forget the cached answer. */
export function resetViewerRole() {
  cached = { token: null, promise: null };
}

/**
 * `{ ready, isSuperAdmin, isFtc }`. Until the answer arrives `ready` is false
 * and the viewer is treated as office staff, so FTC-only cues never flash.
 */
export function useViewerRole() {
  const [role, setRole] = useState(Object.assign({ ready: false }, NOBODY));
  useEffect(() => {
    let alive = true;
    loadViewerRole().then((r) => {
      if (alive) setRole(Object.assign({ ready: true }, r));
    });
    return () => { alive = false; };
  }, []);
  return role;
}
